import { useState } from "react";
import { cn } from "../../../lib/utils";

// Static brand logos for the catalog providers, keyed by the server registry's
// provider key (registry.ts). Bundled in-repo under ./logos so the overview and
// detail views render a real brand mark instead of a two-letter monogram, without
// depending on the catalog payload carrying a `logo` URL. Fetched from Brandfetch
// as square (400×400) icon marks; `.jpg`/`.png` resolve to asset URLs via Vite
// (the SVG plugin yields components, not `<img>`-able URLs).
import asana from "./logos/asana.jpg";
import notion from "./logos/notion.png";
import monday from "./logos/monday.jpg";
import clickup from "./logos/clickup.jpg";
import googleDrive from "./logos/google-drive.jpg";
import dropbox from "./logos/dropbox.png";
import zendesk from "./logos/zendesk.jpg";
import hubspot from "./logos/hubspot.jpg";
// Google Sheets has no Brandfetch brand. The `.image.svg` suffix excludes its
// official mark from vite-plugin-svgr, so Vite returns an asset URL.
import googleSheet from "./logos/google-sheet.image.svg";
import canva from "./logos/canva.jpg";
import trello from "./logos/trello.png";
import salesforce from "./logos/salesforce.jpg";
// OneDrive Business and Personal share Microsoft's official product mark. The
// `.image.svg` suffix excludes it from vite-plugin-svgr.
import oneDrive from "./logos/one-drive.image.svg";
import oneDrivePersonal from "./logos/one-drive-personal.image.svg";
// RSS has no Brandfetch brand. The `.image.svg` suffix excludes the standard
// feed mark from vite-plugin-svgr, so Vite returns an asset URL.
import rss from "./logos/rss.image.svg";

const PROVIDER_LOGOS: Record<string, string> = {
  asana,
  notion,
  monday,
  clickup,
  "google-drive": googleDrive,
  dropbox,
  zendesk,
  hubspot,
  "google-sheet": googleSheet,
  canva,
  trello,
  salesforce,
  "one-drive": oneDrive,
  "one-drive-personal": oneDrivePersonal,
  rss,
};

// The brand mark for a provider: our bundled asset first, then any logo URL the
// catalog carried, else undefined (the caller falls back to the monogram).
function logoForProvider(
  providerKey: string,
  serverLogo?: string
): string | undefined {
  // Empty-string server logos fall through to the monogram, not a broken <img>.
  return PROVIDER_LOGOS[providerKey] ?? (serverLogo || undefined);
}

// Render a provider's real logo, falling back to a two-letter monogram when no
// bundled/served logo exists or the image fails to load. Shared by the overview
// cards and the connection-detail header so both read the same.
export function ProviderLogo({
  providerKey,
  displayName,
  serverLogo,
  className,
}: {
  providerKey: string;
  displayName: string;
  serverLogo?: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const src = logoForProvider(providerKey, serverLogo);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        className={cn("h-9 w-9 shrink-0 rounded-md object-contain", className)}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-sm font-semibold uppercase text-neutral-600",
        className
      )}
    >
      {displayName.slice(0, 2)}
    </span>
  );
}
